/**
 * 响应式国际化组合式函数
 *
 * 解决 t() 函数在模板中不响应语言切换的问题。
 * 通过依赖 locale store 的 langVersion 和 langPacks，
 * 当语言包更新时自动触发组件重新渲染。
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useI18n } from '@/hooks/useI18n'
 * const { t } = useI18n()
 * </script>
 *
 * <template>
 *   <span>{{ t('pages/designCenter.title') }}</span>
 * </template>
 * ```
 */
import { computed } from 'vue'
import { useLocaleStore } from '@/store/locale'
import { t as rawT } from '@/lang'

export function useI18n() {
  const localeStore = useLocaleStore()

  /**
   * 响应式翻译函数
   * 返回一个函数，该函数内部访问了 localeStore 的响应式状态，
   * 确保在模板中使用时能正确追踪依赖并在语言切换后重新渲染
   */
  const t = (key: string, fallback?: string): string => {
    // 访问响应式状态以建立依赖追踪
    // 当 langVersion 或 langPacks 变化时，Vue 会重新执行使用了 t() 的渲染函数
    void localeStore.langVersion
    void localeStore.langPacks
    return rawT(key, fallback)
  }

  /** 当前站点标识 */
  const currentSite = computed(() => localeStore.currentSite)

  /** 当前语言代码 */
  const currentLocale = computed(() => localeStore.currentLocale)

  /** 翻译是否正在加载 */
  const isLoading = computed(() => localeStore.isLoading)

  return {
    t,
    currentSite,
    currentLocale,
    isLoading,
  }
}
